import { createFileRoute } from "@tanstack/react-router";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import { useCallback, useEffect, useState } from "react";
import { ClinicalPageShell, ClinicalStatCard } from "@/components/clinical";
import { CrudEmptyState, CrudShellHeader, Loading } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageUploadField } from "@/components/ImageUploadField";
import { apiGetJson, apiSendJson, apiDelete } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { toast } from "sonner";
import {
  Loader2,
  MoreVertical,
  Pencil,
  Plus,
  Power,
  Trash2,
  ScissorsLineDashed,
  CheckCircle2,
  XCircle,
  Search,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { ServiceCatalogItem, ServiceLocationType } from "@/lib/db-service/service-catalog";
import type { ServiceProfessional } from "@/lib/db-service/service-professionals";

export const Route = createFileRoute("/app/servicios")({
  component: Page,
  head: () => ({ meta: [{ title: "Servicios" }] }),
});

const LOCATION_LABEL: Record<ServiceLocationType, string> = {
  business: "En el local",
  client_address: "A domicilio",
  online: "Online",
};

type FormState = {
  name: string;
  shortDescription: string;
  description: string;
  durationMinutes: string;
  bufferMinutes: string;
  price: string;
  depositRequired: boolean;
  depositAmount: string;
  locationType: ServiceLocationType;
  color: string;
  active: boolean;
  heroImage: string;
  /** SCRUM-411: "group" = clase grupal, gestionada desde /app/clases (sesiones + inscripciones con cupo). */
  sessionType: "individual" | "group";
};

const EMPTY_FORM: FormState = {
  name: "",
  shortDescription: "",
  description: "",
  durationMinutes: "30",
  bufferMinutes: "0",
  price: "0",
  depositRequired: false,
  depositAmount: "",
  locationType: "business",
  color: "",
  active: true,
  heroImage: "",
  sessionType: "individual",
};

function serviceToForm(s: ServiceCatalogItem): FormState {
  return {
    name: s.name,
    shortDescription: s.shortDescription ?? "",
    description: s.description ?? "",
    durationMinutes: String(s.durationMinutes),
    bufferMinutes: String(s.bufferMinutes),
    price: String(s.price),
    depositRequired: Boolean(s.depositRequired),
    depositAmount: s.depositAmount != null ? String(s.depositAmount) : "",
    locationType: s.locationType,
    color: s.color ?? "",
    active: s.active,
    heroImage: s.heroImage ?? "",
    sessionType: s.sessionType,
  };
}

function Page() {
  const tenantId = useWorkspaceTenantId();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<ServiceCatalogItem[]>([]);
  const [professionals, setProfessionals] = useState<ServiceProfessional[]>([]);
  const [links, setLinks] = useState<Array<{ professionalId: string; serviceId: string }>>([]);
  const [editing, setEditing] = useState<ServiceCatalogItem | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [linkedProfessionalIds, setLinkedProfessionalIds] = useState<string[]>([]);
  const [initialLinkedProfessionalIds, setInitialLinkedProfessionalIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ServiceCatalogItem | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [s, p, l] = await Promise.all([
        apiGetJson<{ items: ServiceCatalogItem[] }>("/api/service-admin?resource=services", tenantId),
        apiGetJson<{ items: ServiceProfessional[] }>("/api/service-admin?resource=professionals", tenantId),
        apiGetJson<{ items: Array<{ professionalId: string; serviceId: string }> }>(
          "/api/service-admin?resource=professional-services",
          tenantId,
        ),
      ]);
      setItems(s.items ?? []);
      setProfessionals(p.items ?? []);
      setLinks(l.items ?? []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setLinkedProfessionalIds([]);
    setInitialLinkedProfessionalIds([]);
    setDialogOpen(true);
  }

  function openEdit(item: ServiceCatalogItem) {
    setEditing(item);
    setForm(serviceToForm(item));
    const ids = links.filter((l) => l.serviceId === item.id).map((l) => l.professionalId);
    setLinkedProfessionalIds(ids);
    setInitialLinkedProfessionalIds(ids);
    setDialogOpen(true);
  }

  async function save() {
    if (!tenantId || !form.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        shortDescription: form.shortDescription.trim() || null,
        description: form.description.trim() || null,
        durationMinutes: Number(form.durationMinutes || 30),
        bufferMinutes: Number(form.bufferMinutes || 0),
        price: Number(form.price || 0),
        depositRequired: form.depositRequired,
        depositAmount: form.depositRequired ? Number(form.depositAmount || 0) : 0,
        locationType: form.locationType,
        color: form.color.trim() || null,
        active: form.active,
        heroImage: form.heroImage.trim() || null,
        sessionType: form.sessionType,
      };
      let serviceId = editing?.id;
      if (editing) {
        await apiSendJson(
          `/api/service-admin?resource=services&id=${editing.id}`,
          "PATCH",
          payload,
          tenantId,
        );
      } else {
        const r = await apiSendJson<{ item: ServiceCatalogItem }>(
          "/api/service-admin?resource=services",
          "POST",
          payload,
          tenantId,
        );
        serviceId = r.item.id;
        // El endpoint de creación toma pocos campos — el resto se completa con un PATCH inmediato.
        await apiSendJson(`/api/service-admin?resource=services&id=${serviceId}`, "PATCH", payload, tenantId);
      }
      if (serviceId) {
        // syncProfessionalServiceLinks está pensada por profesional (desiredServiceIds vs
        // currentServiceIds) — acá el eje es al revés (un servicio, N profesionales), así
        // que se hace el mismo diff link/unlink directo por cada profesional afectado.
        const toAdd = linkedProfessionalIds.filter((pid) => !initialLinkedProfessionalIds.includes(pid));
        const toRemove = initialLinkedProfessionalIds.filter((pid) => !linkedProfessionalIds.includes(pid));
        await Promise.all([
          ...toAdd.map((professionalId) =>
            apiSendJson("/api/service-admin?resource=link", "POST", { professionalId, serviceId }, tenantId),
          ),
          ...toRemove.map((professionalId) =>
            apiDelete(
              `/api/service-admin?resource=link&professionalId=${professionalId}&serviceId=${serviceId}`,
              tenantId,
            ),
          ),
        ]);
      }
      toast.success(editing ? "Servicio actualizado" : "Servicio creado");
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(item: ServiceCatalogItem) {
    if (!tenantId) return;
    try {
      await apiSendJson(
        `/api/service-admin?resource=services&id=${item.id}`,
        "PATCH",
        { active: !item.active },
        tenantId,
      );
      toast.success(item.active ? "Desactivado" : "Activado");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error");
    }
  }

  function openDeleteDialog(item: ServiceCatalogItem) {
    setDeleteError(null);
    setDeleteTarget(item);
  }

  function closeDeleteDialog() {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError(null);
  }

  async function confirmDelete() {
    if (!deleteTarget || !tenantId || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiDelete(`/api/service-admin?resource=services&id=${deleteTarget.id}`, tenantId);
      toast.success("Servicio eliminado");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "No se pudo eliminar el servicio";
      setDeleteError(msg);
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  }

  async function deactivateInstead() {
    if (!deleteTarget || !tenantId || deleting) return;
    setDeleting(true);
    try {
      await apiSendJson(
        `/api/service-admin?resource=services&id=${deleteTarget.id}`,
        "PATCH",
        { active: false },
        tenantId,
      );
      toast.success("Servicio desactivado");
      setDeleteTarget(null);
      setDeleteError(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo desactivar");
    } finally {
      setDeleting(false);
    }
  }

  const activeCount = items.filter((s) => s.active).length;
  const normalizedSearch = search.trim().toLowerCase();
  const visibleItems = normalizedSearch
    ? items.filter(
        (s) =>
          s.name.toLowerCase().includes(normalizedSearch) ||
          (s.shortDescription ?? "").toLowerCase().includes(normalizedSearch),
      )
    : items;

  return (
    <ClinicalPageShell offset="none" className="pt-6">
      <MerchantExperienceBar screenId="servicios" />
      <CrudShellHeader
        title="Servicios"
        subtitle={`${activeCount} activos de ${items.length}`}
        count={items.length}
        actions={
          <Button size="sm" className="gap-2" onClick={openCreate}>
            <Plus className="h-4 w-4" /> Nuevo servicio
          </Button>
        }
      />
      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        <CrudEmptyState title="Servicios" description="Todavía no hay servicios en el catálogo." />
      ) : (
        <div className="space-y-4 px-4 pb-8">
          <div className="grid gap-3 sm:grid-cols-3">
            <ClinicalStatCard
              label="Total"
              value={items.length}
              icon={ScissorsLineDashed}
              variant="primary"
            />
            <ClinicalStatCard label="Activos" value={activeCount} icon={CheckCircle2} variant="success" />
            <ClinicalStatCard label="Inactivos" value={items.length - activeCount} icon={XCircle} />
          </div>
          <div className="relative max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar servicio…"
              className="pl-8"
              data-testid="services-search"
            />
          </div>
          {visibleItems.length === 0 ? (
            <p className="text-sm text-muted-foreground">Ningún servicio coincide con "{search}".</p>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {visibleItems.map((s) => {
              const providers = professionals.filter((p) =>
                links.some((l) => l.serviceId === s.id && l.professionalId === p.id),
              );
              return (
                <div
                  key={s.id}
                  className="flex flex-col rounded-xl border bg-card p-4 transition-colors hover:border-primary/30"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        {s.color ? (
                          <span
                            className="h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ background: s.color }}
                          />
                        ) : null}
                        <p className="truncate text-sm font-semibold">{s.name}</p>
                        <span
                          className={cn(
                            "h-2 w-2 shrink-0 rounded-full",
                            s.active ? "bg-emerald-500" : "bg-muted-foreground/40",
                          )}
                          title={s.active ? "Activo" : "Inactivo"}
                        />
                      </div>
                      {s.shortDescription ? (
                        <p className="truncate text-xs text-muted-foreground">{s.shortDescription}</p>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openEdit(s)}
                        title="Editar"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-3.5 w-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => void toggleActive(s)}>
                            <Power className="mr-2 h-3.5 w-3.5" />
                            {s.active ? "Desactivar" : "Activar"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => openDeleteDialog(s)}
                          >
                            <Trash2 className="mr-2 h-3.5 w-3.5" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span>{s.durationMinutes} min</span>
                    {s.bufferMinutes > 0 && <span>+{s.bufferMinutes} min buffer</span>}
                    <span>${s.price.toLocaleString("es-AR")}</span>
                    <span>{LOCATION_LABEL[s.locationType]}</span>
                    {s.depositRequired ? <span>Seña ${s.depositAmount ?? 0}</span> : null}
                  </div>
                  {providers.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {providers.map((p) => (
                        <Badge key={p.id} variant="secondary" className="text-[10px] font-normal">
                          {p.name}
                        </Badge>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar servicio" : "Nuevo servicio"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Descripción corta</Label>
              <Input
                value={form.shortDescription}
                onChange={(e) => setForm((f) => ({ ...f, shortDescription: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Descripción</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Duración (min)</Label>
                <Input
                  type="number"
                  value={form.durationMinutes}
                  onChange={(e) => setForm((f) => ({ ...f, durationMinutes: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Buffer (min)</Label>
                <Input
                  type="number"
                  value={form.bufferMinutes}
                  onChange={(e) => setForm((f) => ({ ...f, bufferMinutes: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Precio</Label>
                <Input
                  type="number"
                  value={form.price}
                  onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Modalidad</Label>
                <Select
                  value={form.locationType}
                  onValueChange={(v) => setForm((f) => ({ ...f, locationType: v as ServiceLocationType }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(LOCATION_LABEL) as ServiceLocationType[]).map((k) => (
                      <SelectItem key={k} value={k}>
                        {LOCATION_LABEL[k]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Color</Label>
                <Input
                  type="color"
                  className="h-9 w-full p-1"
                  value={form.color || "#14b8a6"}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Imagen del servicio</Label>
              <ImageUploadField
                kind="service"
                value={form.heroImage.trim() || null}
                onChange={(url) => setForm((f) => ({ ...f, heroImage: url ?? "" }))}
                tenantId={tenantId}
                disabled={saving}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.depositRequired}
                onCheckedChange={(v) => setForm((f) => ({ ...f, depositRequired: v }))}
              />
              <Label className="!mb-0">Requiere seña</Label>
              {form.depositRequired ? (
                <Input
                  type="number"
                  className="w-28"
                  placeholder="Monto"
                  value={form.depositAmount}
                  onChange={(e) => setForm((f) => ({ ...f, depositAmount: e.target.value }))}
                />
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
              <Label className="!mb-0">Activo</Label>
            </div>
            <div className="flex items-center gap-2" data-testid="service-session-type-toggle">
              <Switch
                checked={form.sessionType === "group"}
                onCheckedChange={(v) => setForm((f) => ({ ...f, sessionType: v ? "group" : "individual" }))}
              />
              <Label className="!mb-0">Es una clase grupal (con cupo)</Label>
            </div>
            {form.sessionType === "group" && editing ? (
              <a
                href={`/app/clases?serviceId=${editing.id}`}
                className="block rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-primary hover:bg-primary/10"
              >
                Gestionar horarios e inscriptos de esta clase →
              </a>
            ) : null}

            <div className="space-y-1.5 border-t pt-3">
              <Label>Profesionales que lo prestan</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {professionals.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={linkedProfessionalIds.includes(p.id)}
                      onCheckedChange={(checked) =>
                        setLinkedProfessionalIds((ids) =>
                          checked ? [...ids, p.id] : ids.filter((id) => id !== p.id),
                        )
                      }
                    />
                    {p.name}
                  </label>
                ))}
                {professionals.length === 0 && (
                  <p className="text-xs text-muted-foreground">No hay profesionales cargados todavía.</p>
                )}
              </div>
            </div>

            <Button className="w-full" disabled={saving} onClick={() => void save()}>
              {saving ? "Guardando…" : editing ? "Guardar cambios" : "Crear servicio"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteTarget != null} onOpenChange={(v) => !v && closeDeleteDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  Solo se puede eliminar un servicio sin turnos asociados. Si ya tiene actividad registrada,
                  desactivalo para ocultarlo del catálogo sin perder el historial.
                </p>
                {deleteError ? (
                  <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-destructive">
                    {deleteError}
                  </p>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            {deleteError ? (
              <Button
                type="button"
                variant="secondary"
                disabled={deleting}
                onClick={() => void deactivateInstead()}
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Desactivar en su lugar"}
              </Button>
            ) : null}
            <AlertDialogAction
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                void confirmDelete();
              }}
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ClinicalPageShell>
  );
}
